import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useSection } from "@/lib/SiteContent";
import { imageUrl } from "@/lib/api";

const DEFAULT_HERO_IMG =
  "https://customer-assets.emergentagent.com/job_admiring-jepsen-11/artifacts/e6eilwtd_Bilde%20Hvaler.png";

const FALLBACK = {
  eyebrow: "Vi gifter oss · 15. august",
  subtitle: "Spjærøy · Hvaler",
  image_id: null,
};

export const Hero = () => {
  const hero = useSection("hero", FALLBACK);
  const bg = hero.image_id ? imageUrl(hero.image_id) : DEFAULT_HERO_IMG;

  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0 });

  useEffect(() => {
    const target = new Date(new Date().getFullYear(), 7, 15, 14, 0, 0);
    if (Date.now() > target.getTime()) target.setFullYear(target.getFullYear() + 1);
    const update = () => {
      const diff = target.getTime() - Date.now();
      if (diff <= 0) return setCountdown({ days: 0, hours: 0, minutes: 0 });
      setCountdown({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff / 3600000) % 24),
        minutes: Math.floor((diff / 60000) % 60),
      });
    };
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full flex items-center justify-center overflow-hidden"
    >
      <div className="absolute inset-0">
        <img src={bg} alt="Hvaler skjærgård" className="w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/40" />
      </div>

      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto fade-in">
        <p
          data-testid="hero-eyebrow"
          className="text-[10px] sm:text-xs uppercase tracking-[0.45em] text-white/85 mb-8 font-light"
        >
          {hero.eyebrow}
        </p>
        <h1
          data-testid="hero-names"
          className="font-serif font-light text-white text-5xl sm:text-6xl md:text-7xl lg:text-[7rem] leading-[0.95] tracking-tight"
        >
          Celine
          <span className="block font-script text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white/90 my-2 md:my-4">
            &amp;
          </span>
          Aleksander
        </h1>
        <div className="mt-8 flex items-center justify-center gap-4">
          <span className="ornament" />
          <p className="text-xs sm:text-sm uppercase tracking-[0.35em] text-white/90 font-light">
            {hero.subtitle}
          </p>
          <span className="ornament" />
        </div>
        <div data-testid="hero-countdown" className="mt-10 grid grid-cols-3 gap-6 sm:gap-12 max-w-md mx-auto">
          {[
            { v: countdown.days, l: "Dager" },
            { v: countdown.hours, l: "Timer" },
            { v: countdown.minutes, l: "Min" },
          ].map((c) => (
            <div key={c.l} className="text-center">
              <div className="font-serif text-3xl sm:text-4xl text-white font-light">
                {String(c.v).padStart(2, "0")}
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/70 mt-1">{c.l}</div>
            </div>
          ))}
        </div>
      </div>

      <a
        href="#historie"
        data-testid="hero-scroll-indicator"
        className="absolute bottom-8 sm:bottom-10 left-1/2 -translate-x-1/2 text-white/80 hover:text-white transition-colors z-10"
        aria-label="Bla nedover"
      >
        <ChevronDown className="animate-bounce" size={28} strokeWidth={1.2} />
      </a>
    </section>
  );
};
